/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Grid, IconButton, Menu, MenuItem, Paper, Typography, 
  Table, TableBody, TableContainer, TableHead, TablePagination, TableRow,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ListTableCell, ListTableRow, FormTitleTypography, FormHeaderPaper, FormDescriptionTypography, NoMaxWidthTooltip} from '../../components/CustomStyles/StyledComponents';
import { copyReport, deleteReport, getAllReports, resetResponseState, setLoading, setReportId, 
         submitStatusReport, unsetLoading, getAdminFarm,  clearAllReportsData, getUserPermissionsForFarm,
        resetFarmReportSettings } from '../../store/appAction';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import toastr from "toastr";
import DeleteReportModal from "../../components/Modal/DeleteReportModal";
import CopyReportModal from "../../components/Modal/CopyReportModal";
import { toastrCustomOptions, toastrCustomOptionsLonger,toastrCustomOptionsNoFarmSelectPopUp } from "../../constants/toastrOptions";
import { UserRole } from '../../helpers/userHelper';
import CommonModal from "../../components/Modal/CommonModal";
import ErrorModal from "../../components/Modal/ErrorModal";
import EmptyTableBody from "../../components/Tables/EmptyTableBody";
import SelectedFarm from '../../components/SelectedFarm/SelectedFarm';
import { endpoints } from '../../api/endpoints';
import * as api from '../../api/base';
import {ReportStatus, ReportHlp} from '../../helpers/reportResultsHelper'
import {AGCReportsPermissions} from '../../helpers/permissionsHelper'

const FarmReport = () => {
  dayjs.extend(utc);
  toastr.options = toastrCustomOptions;
  const dispatch = useDispatch();
  let navigate = useNavigate();

  const farmId = useSelector(state => state.adminFarm.farmId);
  const reportList = useSelector(state => state.farm.reports);
  const { user, successMsg, errorMsg } = useSelector(state => state.common);
  const farmPermissions = useSelector(state => state.auth.farmPermissions);

  const [currentReport, setCurrentReport] = useState(null);
  const [copyReportTitle, setCopyReportTitle] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [commonModalOpen, setCommonModalOpen] = useState(false);
  const [cantValidateModalOpen, setCantValidateModalOpen] = useState(false);
  const [alreadyValidated, setAlreadyValidated] = useState(null)


  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    if(farmId){
      dispatch(getAdminFarm(farmId));
      dispatch(setLoading());
      dispatch(getUserPermissionsForFarm({ farmId }));
      dispatch(getAllReports({ farmId }));
      // dispatch(setReportId(null));
    }
  }, [farmId]);

  useEffect(() => {
    return () => {
      dispatch(clearAllReportsData());
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      dispatch(unsetLoading());
    }, 500);
  }, [reportList]);


  useEffect(() => {
    if (successMsg || errorMsg) {
      dispatch(unsetLoading());
      if (successMsg) {
        toastr.success(successMsg);
        dispatch(getUserPermissionsForFarm({ farmId }));
        dispatch(getAllReports({ farmId }));
      }
      errorMsg && toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
      dispatch(resetResponseState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMsg, errorMsg]);

  const hasPermission = (pfn) =>{
    return Object.values(farmPermissions ?? {}).some(value => pfn(value))
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, report) => {
    setCurrentReport(report)
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditDetails = (e, reportId) => {
    if(hasPermission(AGCReportsPermissions.canEdit)){
      handleMenuClose();
      dispatch(setReportId(reportId || currentReport?.reportId));
      navigate('/data-entry/report/edit')
    }
  }

  const handleSubmitForReview = () => {
    handleMenuClose();
    let alreadyValidated = ReportHlp.findSameYearAlreadyValidated(reportList, currentReport?.reportId);
    if(alreadyValidated){
      setAlreadyValidated(alreadyValidated);
      setCantValidateModalOpen(true);
    }else if ( ReportStatus.isPreCalculation(currentReport?.status))
      setCommonModalOpen(true);
    else if (ReportStatus.canSubmit(currentReport?.status))
      handleSubmitStatus(ReportStatus.ReviewRequested)
  }

  const handleSubmitFinalise = () => {
    handleMenuClose();
    if ( ReportStatus.isPreCalculation(currentReport?.status))
      setCommonModalOpen(true);
    else if (ReportStatus.isCalculated(currentReport?.status))
      handleSubmitStatus(ReportStatus.Finalised)
  }

  const handleSubmitStatus = (statusId) => {
    handleMenuClose();
    dispatch(submitStatusReport({
      reportId: currentReport?.reportId,
      statusId: statusId
    }));
  }

  const handleViewCharts = () => {
    handleMenuClose();
    dispatch(setReportId(currentReport?.reportId));
    navigate('/results/whole-farm/charts')
  }
  const handleCopy = () => {
    handleMenuClose();
    setCopyModalOpen(true);
    // dispatch(setReportId(currentReport));
  }
  const handleDelete = async (reportId) => {
    if(reportId){
      const response = await api.get(endpoints.farmReport.settings.concat('/', reportId));
      if (response && response.status === 200) {
        if(response?.data?.isUpgraded === false){
          handleMenuClose();
          toastr.warning("This farm report cannot be updated as it belongs to a user that has not been approved for Agrecalc cloud.", undefined, toastrCustomOptionsNoFarmSelectPopUp);
        }else{
          handleMenuClose();
          setDeleteModalOpen(true);
        }
      }
    }   
  }

  const handleDeleteModalResponse = (positiveResponse) => {
    setDeleteModalOpen(false);
    if (positiveResponse) {
      dispatch(deleteReport(currentReport?.reportId));
    }
  }

  const handleCopyModalResponse = (positiveResponse) => {
    setCopyModalOpen(false);
    if (positiveResponse) {
      dispatch(setLoading());
      dispatch(copyReport({ reportId: currentReport?.reportId, title: copyReportTitle }));
    }
  }

  const handleCommonModalResponse = (positiveResponse) => {
    setCommonModalOpen(false);
    if (positiveResponse) {
      dispatch(setReportId(currentReport?.reportId));
      navigate('/results/whole-farm/charts')
    }
  }

  const onCreate = ()=>{
      dispatch(setReportId(null));
      navigate('/farm-reports/create')
      dispatch(resetFarmReportSettings());
  }

  const valStatusOptions=[
    {name:'Start Validation', statusId:ReportStatus.ReviewInProgress},
    {name:'Complete And Valid', statusId:ReportStatus.ReviewCompletedAndValid},
    {name:'Complete Not Valid', statusId:ReportStatus.ReviewCompletedAndInvalid},
    {name:'Incomplete', statusId:ReportStatus.CalculationCompleted},
  ] 

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} lg={12}>
        <FormHeaderPaper sx={{ p: 0, display: 'flex', flexDirection: 'column',}}>
          <SelectedFarm />
          <FormTitleTypography variant="h5" component="div">
            Farm Reports
          </FormTitleTypography>
          <FormDescriptionTypography variant="body2" paragraph>
            {'Your farm reports and their status are presented below.'}
          </FormDescriptionTypography>
        </FormHeaderPaper>
      </Grid>
      <Grid item>
        {
          hasPermission(AGCReportsPermissions.canCreate) ?
            <FormDescriptionTypography variant="body2" paragraph>
              {'You can Edit, View Results, Copy, Delete and change the status of reports using the ellipsis under the Actions column. To build a new report please press the Create button.'}
            </FormDescriptionTypography> :
            <FormDescriptionTypography variant="body2" paragraph>
              {'You can Edit, View Results, Copy, Delete and change the status of reports using the ellipsis under the Actions column.'}
            </FormDescriptionTypography>
        }
      </Grid>
      <Grid item xs={12} md={12} lg={12} mt={0}>
        <Paper sx={{ p: 0, flexDirection: 'column', textAlign: 'right' }}>
          { hasPermission(AGCReportsPermissions.canCreate) && 
              <Button variant="contained" sx={{ textTransform: 'none', mr: 5 }} onClick={onCreate}>
              Create
            </Button>
          }
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <TableContainer component={Paper}>
            <Table sx={{ width: '100%' }} size="small" aria-label="customized table">
              <TableHead>
                <TableRow>
                  <ListTableCell sx={{ textAlign: 'left', width: '12%' }}>Report ID</ListTableCell>
                  <ListTableCell sx={{ textAlign: 'left', width: '32%' }}>Name</ListTableCell>
                  <ListTableCell sx={{ width: '18%' }}>Report End Date</ListTableCell>
                  <ListTableCell sx={{ width: '18%' }}>Updated On</ListTableCell>
                  <ListTableCell sx={{ width: '10%' }}>Status
                    <NoMaxWidthTooltip title={ReportStatus.renderStatusTooltip()}>
                      <InfoOutlinedIcon  color='primary' sx={{ marginLeft: '5px', verticalAlign: 'middle' , cursor: 'pointer'}} fontSize='small' />
                    </NoMaxWidthTooltip>
                  </ListTableCell>
                  <ListTableCell sx={{ width: '8%' }}>Actions</ListTableCell>
                </TableRow>
              </TableHead>
              {reportList.length > 0 ?
                <TableBody>
                  {reportList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                    <ListTableRow key={row.reportId}>
                      <ListTableCell
                        sx={{ cursor: 'pointer' }}
                        onClick={(e) => handleEditDetails(e, row.reportId)}
                      >
                        {row.friendlyReportId || '--'}
                      </ListTableCell>
                      <ListTableCell
                        sx={{ cursor: 'pointer' }}
                        onClick={(e) => handleEditDetails(e, row.reportId)}
                      >
                        {row.reportName || '--'}
                      </ListTableCell>
                      <ListTableCell>{dayjs.utc(row.yearEnd).local().format('MMMM YYYY')}</ListTableCell>
                      <ListTableCell>{dayjs.utc(row.lastUpdated || '').local().format('D/M/YY H:mm')}</ListTableCell>
                      { ReportStatus.getColorTableCell(row.status) }
                      <ListTableCell>
                        <IconButton
                          aria-label="more"
                          id="long-button"
                          aria-controls={menuOpen ? 'long-menu' : undefined}
                          aria-expanded={menuOpen ? 'true' : undefined}
                          aria-haspopup="true"
                          onClick={(e) => handleMenuClick(e, row)}
                        >
                          <MoreVertIcon />
                        </IconButton>

                      </ListTableCell>
                    </ListTableRow>
                  ))}
                  <Menu id="long-menu" MenuListProps={{ 'aria-labelledby': 'long-button',}}
                    anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}
                    slotProps={{ paper: { width: '180px', } }}>
                      {
                        hasPermission(AGCReportsPermissions.canEdit) && 
                        <MenuItem onClick={handleEditDetails}>
                          Edit
                        </MenuItem>
                      }
                      {ReportStatus.canSubmit(currentReport?.status) && hasPermission(AGCReportsPermissions.canEdit) &&
                        <MenuItem onClick={()=>handleSubmitFinalise()}>
                          Finalise
                        </MenuItem>}
                      {ReportStatus.canSubmit(currentReport?.status) && hasPermission(AGCReportsPermissions.canSubmit) &&
                        <MenuItem onClick={()=>handleSubmitForReview()}>
                          Send for Validation
                        </MenuItem>}
                       {UserRole.isAllAdmins(user?.role) && ReportStatus.isUnderReview(currentReport?.status)?     
                            valStatusOptions.map((item) => (
                              <MenuItem key={item.statusId} onClick={() => handleSubmitStatus(item.statusId)}>
                               {item.name}
                              </MenuItem>
                            )):''            
                        }
                      <MenuItem onClick={handleViewCharts}>
                        View Results
                      </MenuItem>
                      {
                        hasPermission(AGCReportsPermissions.canCopy) && 
                        <MenuItem onClick={handleCopy}>
                          Copy
                        </MenuItem>
                      }
                      <Tooltip title={ReportStatus.getDeleteTooltip(currentReport?.status)}>
                      <span><MenuItem onClick={()=>{ handleDelete(currentReport?.reportId);}} 
                              disabled={!ReportStatus.canDelete(currentReport?.status)} >
                        Delete
                      </MenuItem>
                      </span>
                      </Tooltip>
                  </Menu>
                </TableBody> : <TableBody><EmptyTableBody title="reports" /></TableBody>}
            </Table>
          </TableContainer>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={reportList.length}
            rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
          <DeleteReportModal isOpen={deleteModalOpen} handleResponse={handleDeleteModalResponse} />
          <CopyReportModal isOpen={copyModalOpen} handleResponse={handleCopyModalResponse} onTitleChange={setCopyReportTitle} />
          <CommonModal isOpen={commonModalOpen}
            value='The data for this report is not yet ready for calculation. Press Ok and you will be shown which data checks are failing.'
            handleResponse={handleCommonModalResponse} />
          <ErrorModal isOpen={cantValidateModalOpen} handleResponse={()=>{setCantValidateModalOpen(false);}} title="Submission Error">
              <div>Can only validate one report per year.</div>
              Report {alreadyValidated?.friendlyReportId}: {alreadyValidated?.reportName} has
              already been validated for year {ReportHlp.getYearFromStr(alreadyValidated?.yearEnd)}.
          </ErrorModal>
        </Paper>
      </Grid>
    </Grid >);
};

export default FarmReport;

